
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentBundle, getExpiredBundles, getPermanentBundles, checkBundlePurchase } from "@/lib/bundles";
import { useAuth } from "@/components/AuthProvider";

export function useBundles(enabled: boolean = true) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const { data: activeBundle, isLoading: isActiveBundleLoading, isError: isActiveBundleError, refetch: refetchActiveBundle } = useQuery({
    queryKey: ['activeBundle'],
    queryFn: getCurrentBundle,
    enabled,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0
  });

  const { data: permanentBundles = [], isLoading: isPermanentBundlesLoading, isError: isPermanentBundlesError, refetch: refetchPermanentBundles } = useQuery({
    queryKey: ['permanentBundles'],
    queryFn: getPermanentBundles,
    enabled,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0
  });

  const { data: expiredBundles = [], isLoading: isExpiredBundlesLoading, isError: isExpiredBundlesError } = useQuery({
    queryKey: ['expiredBundles'],
    queryFn: getExpiredBundles,
    enabled,
    retry: 2,
    refetchInterval: false,
    refetchOnWindowFocus: false
  });

  // Check access for active temporal bundle
  const { data: hasActiveAccess, refetch: refetchActiveAccess, isLoading: isActiveAccessLoading } = useQuery({
    queryKey: ['bundlePurchase', activeBundle?.id],
    queryFn: () => activeBundle ? checkBundlePurchase(activeBundle.id) : Promise.resolve(false),
    enabled: enabled && !!activeBundle && !!session,
    retry: 1,
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  // Check access for permanent bundles
  const { data: permanentBundleAccess = {}, isLoading: isPermanentAccessLoading, refetch: refetchPermanentAccess } = useQuery({
    queryKey: ['permanentBundleAccess', permanentBundles.map(b => b.id).join(',')],
    queryFn: async () => {
      if (!permanentBundles.length || !session) return {};
      
      try {
        const accessMap: Record<number, boolean> = {};
        await Promise.all(
          permanentBundles.map(async (bundle) => {
            try {
              const hasAccess = await checkBundlePurchase(bundle.id);
              accessMap[bundle.id] = hasAccess;
            } catch (error) {
              console.error(`Error checking access for permanent bundle ${bundle.id}:`, error);
              accessMap[bundle.id] = false;
            }
          })
        );
        return accessMap;
      } catch (error) {
        console.error("Error checking permanent bundle access:", error);
        return {};
      }
    },
    enabled: enabled && !!session && permanentBundles.length > 0,
    retry: 1,
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  const { data: expiredBundleAccess = {}, isLoading: isExpiredAccessLoading, isError: isExpiredAccessError, refetch: refetchExpiredAccess } = useQuery({
    queryKey: ['expiredBundleAccess', expiredBundles.map(b => b.id).join(',')],
    queryFn: async () => {
      if (!expiredBundles.length || !session) return {};
      
      try {
        const accessMap: Record<number, boolean> = {};
        await Promise.all(
          expiredBundles.map(async (bundle) => {
            try {
              const hasAccess = await checkBundlePurchase(bundle.id);
              accessMap[bundle.id] = hasAccess;
            } catch (error) {
              console.error(`Error checking access for bundle ${bundle.id}:`, error);
              accessMap[bundle.id] = false;
            }
          })
        );
        return accessMap;
      } catch (error) {
        console.error("Error checking expired bundle access:", error);
        return {};
      }
    },
    enabled: enabled && !!session && expiredBundles.length > 0,
    retry: 1,
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  // Improved refreshAll function that forces a complete refresh
  const refreshAll = async () => {
    // First invalidate all queries
    queryClient.invalidateQueries({ queryKey: ['activeBundle'] });
    queryClient.invalidateQueries({ queryKey: ['permanentBundles'] });
    queryClient.invalidateQueries({ queryKey: ['expiredBundles'] });
    queryClient.invalidateQueries({ queryKey: ['bundlePurchase'] });
    queryClient.invalidateQueries({ queryKey: ['permanentBundleAccess'] });
    queryClient.invalidateQueries({ queryKey: ['expiredBundleAccess'] });
    
    // Then force refetch each query
    await Promise.all([
      refetchActiveBundle(),
      refetchPermanentBundles(),
      refetchActiveAccess?.(),
      refetchPermanentAccess?.(),
      refetchExpiredAccess?.()
    ]);
  };

  const isLoading = 
    isActiveBundleLoading || 
    isPermanentBundlesLoading ||
    isExpiredBundlesLoading || 
    isExpiredAccessLoading ||
    isPermanentAccessLoading ||
    isActiveAccessLoading;
    
  const isError = 
    isActiveBundleError || 
    isPermanentBundlesError ||
    isExpiredBundlesError || 
    isExpiredAccessError;

  return {
    activeBundle,
    permanentBundles,
    expiredBundles,
    hasActiveAccess,
    permanentBundleAccess,
    expiredBundleAccess,
    refetchActiveAccess,
    refetchPermanentAccess,
    refreshAll,
    queryClient,
    isLoading,
    isError
  };
}
